import Avo, { AvoEnv, NavigationType, NavigationTypeType, Products, DeviceCategoryValueType } from "./jacksonAvo";
import jacksonData from "./data-map/jacksonData";

declare global {
  interface Window {
    analytics: any;
    dataLayer: any;
    navigationTracking: {
      navigationFlyout?: string;
      navigationGroup?: string;
    };
  }
}

let segmentAnalytics: any;

// --- Attribution persistence -------------------------------------------------

const ATTR_KEY = 'jxn_attribution';
const ATTR_COOKIE = 'jxn_attr';
const ATTR_TTL_MS = 30 * 60 * 1000; // 30-minute TTL; set to 0 to disable

function sameDomainReferrer(): boolean {
  try {
    if (!document.referrer) return false; // treat direct as landing
    const ru = new URL(document.referrer);
    return ru.hostname === location.hostname;
  } catch {
    return false;
  }
}

function parseAttributionFromURL() {
  const p = new URLSearchParams(location.search);
  const pick = (k: string) => {
    const v = p.get(k);
    return v && v.trim() ? v : null;
  };
  const attr: Record<string, string> = {};
  const keys = ['utm_medium', 'utm_source', 'utm_campaign', 'utm_channel', 'utm_id', 'utm_term'];
  keys.forEach(k => {
    const v = pick(k);
    if (v) attr[k] = v;
  });
  const src = pick('src');
  if (src) attr.src = src;
  return Object.keys(attr).length ? attr : null;
}

function writeAttrCookie(obj: any) {
  try {
    const v = encodeURIComponent(JSON.stringify(obj));
    document.cookie = `${ATTR_COOKIE}=${v}; Path=/; Max-Age=${60 * 60 * 24 * 30}; SameSite=Lax`;
  } catch {}
}

function readAttrCookie() {
  try {
    const m = document.cookie
      .split(';')
      .map(s => s.trim())
      .find(s => s.startsWith(`${ATTR_COOKIE}=`));
    if (!m) return null;
    const raw = decodeURIComponent(m.split('=')[1]);
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function setStoredAttribution(obj: any) {
  const payload = { ...obj, ts: Date.now() };
  try {
    localStorage.setItem(ATTR_KEY, JSON.stringify(payload));
  } catch {}
  writeAttrCookie(payload);
}

function getStoredAttribution() {
  let obj: any = null;
  try {
    const raw = localStorage.getItem(ATTR_KEY);
    if (raw) obj = JSON.parse(raw);
  } catch {}
  if (!obj) obj = readAttrCookie();
  if (!obj) return null;

  if (ATTR_TTL_MS > 0 && obj.ts && Date.now() - obj.ts > ATTR_TTL_MS) {
    return null; // TTL expired
  }
  return obj;
}

function persistAttributionOnLanding() {
  // Only overwrite if this is a landing (no same-domain referrer) and URL has utm/src
  const isLanding = !sameDomainReferrer();
  const urlAttrs = parseAttributionFromURL();
  if (!urlAttrs || !isLanding) return;
  setStoredAttribution(urlAttrs);
}

function getAttribution() {
  // Prefer stored; fall back to current URL if present
  return getStoredAttribution() || parseAttributionFromURL() || {};
}

function getSourceCode(): string | null {
  const a: any = getAttribution();
  return a.src || null;
}

// -----------------------------------------------------------------------------
// DataLayer helper

function pushToDataLayer(eventName: string, payload: Record<string, any>) {
  window.dataLayer = window.dataLayer || [];
  const cleaned = Object.fromEntries(
    Object.entries(payload).filter(([, v]) => v !== undefined)
  );
  window.dataLayer.push({
    event: eventName,
    ...cleaned
  });
}

const EVENT_NAME_MAP: Record<string, string> = {
  productViewed: 'Product Viewed',
  cartViewed: 'Cart Viewed',
  navigationClicked: 'Navigation Clicked',
  dealerSearched: 'Dealer Searched',
  fileDownloaded: 'File Downloaded',
  productSearched: 'Product Searched',
  filterClicked: 'Filter Clicked',
  promotionClicked: 'Promotion Clicked',
  recommendationClicked: 'Recommendation Clicked',
  notifyInStock: 'Notify In Stock',
  loadedAPage: 'Loaded A Page',
  loggedIn: 'Logged In',
  loggedOut: 'Logged Out',
  detailsCaptured: 'Details Captured'
};

// --- Segment / Avo setup ------------------------------------------------------

function setShopifyCustomPixelValues() {
  const jwtAll = document.getElementById('react-jwt-all');
  let segmentWriteKey = jacksonData.segmentProd;
  if (window.location.href.includes('garage')) {
    segmentWriteKey = jacksonData.segmentDev;
  }

  const shopifyCustomerID = jwtAll?.getAttribute('data-customer') || '';
  localStorage.setItem('shopifyCustomerID', shopifyCustomerID);
  localStorage.setItem('siteID', 'jackson_shop');
  localStorage.setItem('segmentWriteKey', segmentWriteKey);
}

function loadSegmentAnalytics() {
  let segmentWriteKey = jacksonData.segmentProd;
  if (window.location.href.includes('garage')) {
    segmentWriteKey = jacksonData.segmentDev;
  }

  segmentAnalytics = window.analytics = window.analytics || [];

  if (segmentAnalytics.initialize || segmentAnalytics.invoked) {
    initializeAvo();
    return;
  }

  segmentAnalytics.invoked = true;
  segmentAnalytics.methods = [
    "trackSubmit", "trackClick", "trackLink", "trackForm", "pageview",
    "identify", "reset", "group", "track", "ready", "alias", "page",
    "once", "off", "on"
  ];

  segmentAnalytics.factory = function (t: string) {
    return function () {
      const e = Array.prototype.slice.call(arguments);
      e.unshift(t);
      segmentAnalytics.push(e);
      return segmentAnalytics;
    };
  };

  for (let t = 0; t < segmentAnalytics.methods.length; t++) {
    const e = segmentAnalytics.methods[t];
    segmentAnalytics[e] = segmentAnalytics.factory(e);
  }

  segmentAnalytics.load = function (t: string) {
    const e = document.createElement("script");
    e.type = "text/javascript";
    e.async = true;
    e.src = (document.location.protocol === "https:" ? "https://" : "http://") +
            "cdn.segment.com/analytics.js/v1/" + t + "/analytics.min.js";
    const n = document.getElementsByTagName("script")[0];
    if (n.parentNode) {
      n.parentNode.insertBefore(e, n);
    }
  };

  segmentAnalytics.SNIPPET_VERSION = "3.1.0";
  segmentAnalytics.load(segmentWriteKey);
  if (window.location.href.includes('garage')) {
    console.log('Segment SDK Loaded');
  }

  initializeAvo();
}

function initializeAvo() {
  const webDebugger = document.getElementById('segment-source')?.getAttribute('data-avo-web-debugger') === 'true';

  const customDestination = {
    logEvent: (eventName: string, eventProperties: object) => {
      segmentAnalytics.track(eventName, eventProperties);

      const dlEventName =
        EVENT_NAME_MAP[eventName] ||
        (eventName.includes(' ')
          ? eventName.trim()
          : eventName.replace(/([A-Z])/g, ' $1').trim());
      const cleanDlEventName = dlEventName.replace(/\s{2,}/g, ' ');

      pushToDataLayer(cleanDlEventName, eventProperties);
    },
    logPage: (pageName: string, pageProperties: object) => {
      segmentAnalytics.page(pageName, pageProperties);
      pushToDataLayer('Page Viewed', { pageName, ...pageProperties });
    },
    setUserProperties: (userId: string, userProperties: object) => {
      if (userId) segmentAnalytics.identify(userId, userProperties);
      pushToDataLayer('User Properties Set', { userId, ...userProperties });
    },
    identify: (_userId: string) => {},
    unidentify: () => {
      segmentAnalytics.reset();
      pushToDataLayer('User Unidentified', {});
    },
    setGroupProperties: (groupType: string, groupId: string, groupProperties: object) => {
      segmentAnalytics.group(groupId, groupProperties);
      pushToDataLayer('Group Properties Set', { groupType, groupId, ...groupProperties });
    },
    addCurrentUserToGroup: (groupType: string, groupId: string) => {
      segmentAnalytics.group(groupId);
      pushToDataLayer('Added User To Group', { groupType, groupId });
    },
    logEventWithGroups: () => {}
  };

  let device: DeviceCategoryValueType = 'desktop';
  const ua = navigator.userAgent.toLowerCase();
  if (/mobile|android|iphone|ipod/.test(ua)) device = 'mobile';
  else if (/tablet|ipad/.test(ua)) device = 'tablet';

  const locale = document.querySelector('header')?.getAttribute('data-locale') || 'en_US';
  let jacksonMarketId = 'jackson_emea';
  if (locale.includes('US')) jacksonMarketId = 'jackson_us';
  else if (locale.toLowerCase().includes('japan')) jacksonMarketId = 'jackson_jp';
  else if (locale.toLowerCase().includes('australia')) jacksonMarketId = 'jackson_au';

  Avo.initAvo(
    { env: AvoEnv.Dev, webDebugger },
    { deviceCategory: device, siteId: "jackson_shop", marketId: jacksonMarketId },
    customDestination,
    customDestination
  );
}

// --- Utilities ----------------------------------------------------------------

function getUTMParameter(param: string) {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.has(param) ? urlParams.get(param) : null;
}

// --- Common data ---------------------------------------------------------------

function collectCommonData() {
  const prsID = localStorage.getItem('prsID') || '';
  const cartData = localStorage.getItem('cart');
  const productData = localStorage.getItem('product') ? JSON.parse(localStorage.getItem('product') as string) : null;
  const collectionData = localStorage.getItem('collection') ? JSON.parse(localStorage.getItem('collection') as string) : null;
  const category = collectionData?.title || '';
  const currency = document.querySelector('#segment-source')?.getAttribute('data-currency') || 'USD';
  const path = window.location.pathname;
  const url = window.location.href;
  const referrer = document.referrer || null;
  const date = new Date().toISOString();
  const headerElement = document.querySelector('header');
  const locale = headerElement?.getAttribute('data-locale') || 'en_US';
  const region = headerElement?.getAttribute('data-region') || 'US';
  let name = headerElement?.getAttribute('data-page-name') || null;
  const customer = headerElement?.getAttribute('data-customer') || '';
  localStorage.setItem('shopifyLocale', locale);

  if (!name) {
    const cleanUrl = url.split('?')[0];
    const parts = cleanUrl.split('/');
    if (parts.includes('blogs')) {
      name = 'blogs';
    } else {
      name = parts.length <= 3 || parts[parts.length - 1] === '' ? 'home' : parts[parts.length - 1];
    }
  }

  // Attribution (persisted or from URL)
  const attr: any = getAttribution();

  return {
    cartData,
    productData,
    currency,
    path,
    url,
    referrer,
    date,
    locale,
    region,
    name,
    customer,
    utmMedium: attr.utm_medium || null,
    utmChannel: attr.utm_channel || null,
    utmCampaign: attr.utm_campaign || null,
    utmSource: attr.utm_source || null,
    utmId: attr.utm_id || null,
    utmTerm: attr.utm_term || null,
    category,
    prsID,
    sourceCode: getSourceCode()
  };
}

function parseCents(v: string | number | null | undefined): number {
  if (v == null) return 0;
  const s = String(v).trim();
  if (/[.,]/.test(s)) {
    const n = Number(s.replace(/,/g, ""));
    return Number.isFinite(n) ? n : 0;
  }
  const n = Number(s);
  return Number.isFinite(n) ? Math.round(n) / 100 : 0;
}

function formatCategory(s: string): string {
  if (!s) return "";
  return s
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, c => c.toUpperCase());
}

function pickCategory({
  collectionsAttr,
  parsedCategory,
  parsedTags,
  fetchedType,
  fetchedTags,
  collectionHandle
}: {
  collectionsAttr?: string;
  parsedCategory?: string;
  parsedTags?: string[] | string;
  fetchedType?: string;
  fetchedTags?: string[] | string;
  collectionHandle?: string;
}): string {
  const clean = (x?: string) => (x || "").trim();
  const fromCollections = (collectionsAttr || "")
    .split(",")
    .map(s => s.trim())
    .filter(Boolean)
    .filter(c => !/compare-test|discontinued-products/i.test(c))[0];
  if (fromCollections) return formatCategory(fromCollections);
  if (clean(parsedCategory)) return formatCategory(parsedCategory!);
  const ptags = Array.isArray(parsedTags) ? parsedTags : (parsedTags ? String(parsedTags).split(",") : []);
  if (ptags[0]) return formatCategory(ptags[0]);
  if (clean(fetchedType)) return formatCategory(fetchedType!);
  const ftags = Array.isArray(fetchedTags) ? fetchedTags : (fetchedTags ? String(fetchedTags).split(",") : []);
  if (ftags[0]) return formatCategory(ftags[0]);
  if (clean(collectionHandle)) return formatCategory(collectionHandle!);
  return "";
}

let _collectionProductsCache: Record<string, Promise<any[]> | undefined> = {};
async function fetchCollectionProducts(collectionHandle: string): Promise<any[]> {
  if (!collectionHandle) return [];
  if (_collectionProductsCache[collectionHandle]) return _collectionProductsCache[collectionHandle]!;
  _collectionProductsCache[collectionHandle] = (async () => {
    try {
      const res = await fetch(`/collections/${collectionHandle}/products.json?limit=250`, { credentials: "same-origin" });
      if (!res.ok) return [];
      const data = await res.json();
      return Array.isArray(data?.products) ? data.products : [];
    } catch {
      return [];
    }
  })();
  return _collectionProductsCache[collectionHandle]!;
}

const handleProductListViewed = async (commonData: any) => {
  if (!/\/collections\//.test(location.pathname)) return;
  const list = document.querySelector(".boost-sd__product-list");
  if (!list) return;

  const items = Array.from(list.querySelectorAll<HTMLElement>(".boost-sd__product-item[data-product-id]"));
  if (!items.length) return;

  const collectionHandle = (location.pathname.match(/\/collections\/([^/?#]+)/) || [])[1] || "";
  const currency = (window as any).Shopify?.currency?.active || "USD";

  const rows = items
    .map((item, idx) => {
      const productId = item.getAttribute("data-product-id") || "";
      if (!productId) return null;

      let handle = "";
      let variant = "";
      let price: number | null = null;

      let productName =
        (item.querySelector('.boost-sd__product-image img[alt]') as HTMLImageElement | null)?.alt?.trim() || "";

      let parsed: any = null;
      try {
        const raw = item.getAttribute("data-product");
        parsed = raw ? JSON.parse(raw) : null;
        handle = parsed?.handle || "";
        if (parsed?.priceMin != null || parsed?.priceMax != null) {
          price = parseCents(parsed.priceMin ?? parsed.priceMax);
        }
        if (parsed?.variants) {
          try {
            const arr = JSON.parse(parsed.variants);
            variant = String(arr?.[0]?.id || "");
          } catch {}
        }
      } catch {}

      if (!productName && handle) {
        const titleEl = document.querySelector(
          `a.boost-sd__product-link[href*="/products/${handle}"] .boost-sd__product-title`
        ) as HTMLElement | null;
        productName = titleEl?.textContent?.trim() || "";
      }

      if (price == null || !Number.isFinite(price)) {
        const klarna = handle
          ? (document.querySelector(
              `a.boost-sd__product-link[href*="/products/${handle}"] .klarna-placement-pdp`
            ) as HTMLElement | null)
          : null;
        const pa = klarna?.getAttribute("data-purchase-amount");
        if (pa) price = parseCents(pa);
      }
      if (price == null || !Number.isFinite(price)) {
        const priceText = handle
          ? (document.querySelector(
              `a.boost-sd__product-link[href*="/products/${handle}"] .boost-sd__product-price .boost-sd__format-currency`
            ) as HTMLElement | null)?.textContent?.match(/[\d.,]+/)?.[0]
          : item
              .querySelector(".boost-sd__product-price .boost-sd__format-currency")
              ?.textContent?.match(/[\d.,]+/)?.[0];
        price = parseCents(priceText ?? null);
      }

      const collectionsEl = item.matches('[data-limoniapps-discountninja-product-collections]')
        ? item
        : item.querySelector('[data-limoniapps-discountninja-product-collections]');
      const collectionsAttr = collectionsEl?.getAttribute('data-limoniapps-discountninja-product-collections') || "";

      const category = pickCategory({
        collectionsAttr,
        parsedCategory: parsed?.category,
        parsedTags: parsed?.tags,
        collectionHandle
      });

      return {
        currency,
        price: Number.isFinite(price as number) ? (price as number) : 0,
        compareAtPrice: 0,
        quantity: 1,
        sku: "",
        variant,
        category,
        position: idx + 1,
        productName,
        productType: "physical",
        upsell: false,
        productId,
        handle,
        _el: item
      };
    })
    .filter((p): p is NonNullable<typeof p> => Boolean(p && p.productId && p.productName));

  if (!rows.length) return;

  const collectionProducts = await fetchCollectionProducts(collectionHandle);
  const byId = new Map<string, any>();
  const byHandle = new Map<string, any>();
  for (const p of collectionProducts) {
    byId.set(String(p.id), p);
    if (p.handle) byHandle.set(String(p.handle), p);
  }

  for (const row of rows) {
    const pJson = byId.get(row.productId) || (row.handle ? byHandle.get(row.handle) : undefined);
    if (!pJson) continue;

    const vList: any[] = Array.isArray(pJson.variants) ? pJson.variants : [];
    let v =
      (row.variant && vList.find(v => String(v.id) === String(row.variant))) ||
      vList.find(v => !!v.available) ||
      vList[0];

    if (!row.category) {
      row.category = pickCategory({
        fetchedType: pJson.product_type,
        fetchedTags: pJson.tags,
        collectionHandle
      });
    }

    if (v) {
      row.variant = String(v.id || row.variant || "");
      row.sku = String(v.sku || "");
      const fetchedPrice = parseCents(v.price);
      const fetchedCompare = parseCents(v.compare_at_price);
      if (Number.isFinite(fetchedPrice) && fetchedPrice > 0) row.price = fetchedPrice;
      if (Number.isFinite(fetchedCompare) && fetchedCompare > 0) row.compareAtPrice = fetchedCompare;

      try {
        row._el?.setAttribute("data-sku", row.sku);
        row._el?.setAttribute("data-price", String(row.price));
        if (row.compareAtPrice) row._el?.setAttribute("data-compare-at-price", String(row.compareAtPrice));
        row._el
          ?.querySelectorAll('a.boost-sd__product-link')
          ?.forEach(a => {
            a.setAttribute("data-sku", row.sku);
            a.setAttribute("data-price", String(row.price));
            if (row.compareAtPrice) a.setAttribute("data-compare-at-price", String(row.compareAtPrice));
          });
      } catch {}
    }
  }

  const products = rows.map(({ _el, handle, ...rest }) => rest);
  const categoryTag = Array.from(new Set(products.map(p => p.category).filter(Boolean)));

  Avo.productListViewed({
    path: commonData.path ?? location.pathname,
    url: commonData.url ?? location.href,
    referrer: commonData.referrer ?? document.referrer ?? "",
    date: commonData.date ?? new Date().toISOString(),
    locale: commonData.locale ?? (document.documentElement.lang || "en-US"),
    name: commonData.name ?? (document.title || "Product List"),
    sectionId: "shop",
    utmMedium:  commonData.utmMedium ?? null,
    utmChannel: commonData.utmChannel ?? null,
    utmCampaign: commonData.utmCampaign ?? null,
    utmSource:  commonData.utmSource ?? null,
    utmId:      commonData.utmId ?? null,
    utmTerm:    commonData.utmTerm ?? null,
    sourceCode: commonData.sourceCode ?? getSourceCode(),
    products,
    categoryTag
  });
};

const handleLoadedAPage = (data: any) => {
  const { path, url, referrer, date, locale, utmMedium, utmChannel, utmCampaign, utmSource, utmTerm, utmId, sourceCode } = data;
  const name = 'Loaded A Page';
  if (!!document.querySelector('header')) {
    Avo.loadedAPage({
      name,
      path,
      url,
      referrer,
      date,
      locale,
      utmMedium,
      utmChannel,
      utmCampaign,
      utmSource,
      utmTerm,
      utmId,
      customDestinationPageName_: name,
      sectionId: 'shop',
      sourceCode
    });
  }
};

function handleProductViewed(data: any) {
  const { name, productData, path, url, referrer, date, locale, utmMedium, utmChannel, utmCampaign, utmSource, utmId, utmTerm, currency, sourceCode } = data;

  const getVariantFromQueryString = () => {
    const params = new URLSearchParams(window.location.search);
    return params.get('variant') || productData?.variants?.[0]?.id?.toString() || '';
  };

  function getProductTitle() {
    let titleElement = document.querySelector('.product__title h1') ||
                       document.querySelector('.product__title a h2');
    return titleElement?.textContent?.trim() || productData?.title || '';
  }

  function getSkuByVariant() {
    const variantId = getVariantFromQueryString();
    const selectedVariant = productData?.variants?.find((variant: { id: number }) => variant.id.toString() === variantId);
    return selectedVariant?.sku || productData?.variants?.[0]?.sku || '';
  }

  const getPrice = () => {
    let priceElement = document.querySelector('.price-main .price__sale, .price-main .price-item--sale');
    if (!priceElement) {
      priceElement = document.querySelector('.price-main .price-item--regular, .price-main .money');
    }
    if (!priceElement || !priceElement.textContent) return null;

    const rawText = priceElement.textContent.trim();
    const match = rawText.match(/[\d.,]+/);
    if (!match) return null;

    const numericChunk = match[0];
    const lastDotPos = numericChunk.lastIndexOf('.');
    const lastCommaPos = numericChunk.lastIndexOf(',');

    if (lastDotPos === -1 && lastCommaPos === -1) {
      const parsed = parseFloat(numericChunk);
      return isNaN(parsed) ? null : round2(parsed);
    }

    const decimalSeparator = lastDotPos > lastCommaPos ? '.' : ',';
    const separatorIndex = decimalSeparator === '.' ? lastDotPos : lastCommaPos;

    let integerPart = separatorIndex !== -1 ? numericChunk.slice(0, separatorIndex) : numericChunk;
    let fractionPart = separatorIndex !== -1 ? numericChunk.slice(separatorIndex + 1) : '';

    integerPart = integerPart.replace(/[.,]/g, '');
    if (!fractionPart) fractionPart = '0';

    const normalized = `${integerPart}.${fractionPart}`;
    const parsedPrice = parseFloat(normalized);
    return isNaN(parsedPrice) ? null : round2(parsedPrice);

    function round2(num: any) {
      return Math.round((num + Number.EPSILON) * 100) / 100;
    }
  };

  const fireProductViewedEvent = () => {
    loadSegmentAnalytics();
    if (window.location.href.includes('garage')) {
      console.log(productData);
    }
    if (document.querySelector('section[data-product-viewed]') && productData) {
      const product = {
        currency: currency,
        price: getPrice() || 0,
        quantity: 1,
        variant: getVariantFromQueryString(),
        category: productData.type || '',
        position: 1,
        productName: getProductTitle(),
        productType: productData?.type?.includes('Software') ? 'digital' : 'physical',
        upsell: false,
        sku: getSkuByVariant(),
        productId: productData.id
      };

      Avo.productViewed({
        path,
        url,
        name,
        referrer,
        utmMedium,
        utmChannel,
        utmCampaign,
        utmId,
        utmTerm,
        date,
        utmSource,
        locale,
        products: [product],
        categoryTag: productData.tags || [],
        sectionId: 'shop',
        sourceCode
      });
    }
  };

  const modifyHistoryMethod = (type: 'pushState' | 'replaceState') => {
    const original = history[type];
    return function (this: History) {
      const result = original.apply(this, arguments as unknown as [any, string, (string | URL | null | undefined)?]);
      window.dispatchEvent(new Event(type));
      return result;
    };
  };

  history.pushState = modifyHistoryMethod('pushState');
  history.replaceState = modifyHistoryMethod('replaceState');

  fireProductViewedEvent();

  window.addEventListener('popstate', fireProductViewedEvent);
  window.addEventListener('pushState', fireProductViewedEvent);
  window.addEventListener('replaceState', fireProductViewedEvent);
}

function handleCartViewed(data: any) {
  const { cartData, path } = data;
  if (path.includes('/cart') && cartData) {
    const cart = JSON.parse(cartData);
    const productsArray: Products[] = cart.items.map((item: any, index: number) => ({
      currency: cart.currency,
      price: item.price / 100,
      quantity: item.quantity,
      variant: item.variant_id,
      category: item.product_type || '',
      position: index + 1,
      productName: item.product_title,
      productType: item?.type?.includes('Software') ? 'digital' : 'physical',
      upsell: false,
      sku: item.sku,
      productId: item.product_id
    }));

    loadSegmentAnalytics();
    Avo.cartViewed({
      products: productsArray,
      total: cart.total_price / 100,
      subtotal: cart.items_subtotal_price / 100,
      currency: cart.currency,
      path: data.path,
      url: data.url,
      referrer: data.referrer,
      utmMedium: data.utmMedium,
      utmChannel: data.utmChannel,
      utmCampaign: data.utmCampaign,
      utmTerm: data.utmTerm,
      utmId: data.utmId,
      date: data.date,
      utmSource: data.utmSource,
      locale: data.locale,
      name: 'Cart',
      sectionId: 'shop',
      sourceCode: data.sourceCode
    });
  }
}

function handleNavigationClicked(data: any) {
  const { name, path, url, referrer, date, locale, utmMedium, utmChannel, utmCampaign, utmSource, utmId, utmTerm, sourceCode } = data;
  const desktopMenuContent = document.getElementById("contentful-desktop-menu-target");
  if (desktopMenuContent) {
    desktopMenuContent.addEventListener("click", (event: Event) => {
      loadSegmentAnalytics();
      if (window.location.href.includes('garage')) {
        console.log('MenuContent clicked');
      }
      const target = event.target as HTMLElement;

      let navigationType: NavigationTypeType[keyof typeof NavigationType] = NavigationType.MAIN;
      let navigationFlyout: string | null = target.closest(".level-1-wrapper")?.querySelector("[data-navigation-flyout]")?.getAttribute("data-navigation-flyout") || '';
      let navigationGroup: string | null = target.closest(".level-2-wrapper")?.querySelector("[data-navigation-group]")?.getAttribute("data-navigation-group") || '';
      let navigationCore: string | null = target.closest("details[data-navigation-core]")?.getAttribute("data-navigation-core") || '';
      if (navigationGroup) {
        navigationType = NavigationType.SUB;
      }
      if (!navigationGroup && !navigationFlyout) {
        navigationType = NavigationType.SHOP;
      }
      if (target.tagName === "IMG") {
        navigationType = NavigationType.IMAGE;
      }
      if (window.location.href.includes('garage')) {
        console.log(target.tagName);
      }
      if (target.tagName === "SPAN" && (target.classList.contains("level-1-target") || target.classList.contains("level-3-target"))) {
        Avo.navigationClicked({
          path,
          url,
          referrer,
          utmMedium,
          utmChannel,
          utmCampaign,
          utmId,
          utmTerm,
          date,
          name,
          utmSource,
          locale,
          navigationGroup: navigationGroup,
          navigationType: navigationType,
          navigationFlyout: navigationFlyout,
          navigationLabel: target.innerText,
          navigationCore: 'Shop',
          sectionId: 'shop',
          sourceCode
        });
      }
    });
  }

  const mobileMenuContent = document.getElementById("contentful-mobile-menu-render-target");
  if (mobileMenuContent) {
    if (!window.navigationTracking) {
      window.navigationTracking = {};
    }

    let navigationType: NavigationTypeType[keyof typeof NavigationType] = NavigationType.MAIN;
    let navigationFlyout: string | null = '';
    let navigationGroup: string | null = '';
    mobileMenuContent.addEventListener("click", (event: Event) => {
      loadSegmentAnalytics();
      if (window.location.href.includes('garage')) {
        console.log('MenuContent clicked');
      }
      const target = event.target as HTMLElement;

      if (target.closest(".level-1")) {
        navigationFlyout = target.innerText || '';
        window.navigationTracking.navigationFlyout = navigationFlyout;
        window.navigationTracking.navigationGroup = '';
      }
      if (target.closest(".level-2")) {
        navigationGroup = target.innerText || '';
        window.navigationTracking.navigationGroup = navigationGroup;
      }
      if (navigationGroup) {
        navigationType = NavigationType.SUB;
      }
      if (!navigationGroup && !navigationFlyout) {
        navigationType = NavigationType.SHOP;
      }
      if (target.tagName === "IMG") {
        navigationType = NavigationType.IMAGE;
      }
      if (window.location.href.includes('garage')) {
        console.log(target.tagName);
      }
      if (target.tagName === "SPAN" || target.tagName === "A") {
        Avo.navigationClicked({
          path,
          url,
          referrer,
          utmMedium,
          utmChannel,
          utmCampaign,
          utmId,
          utmTerm,
          date,
          name,
          utmSource,
          locale,
          navigationGroup: window.navigationTracking.navigationGroup,
          navigationType: navigationType,
          navigationFlyout: window.navigationTracking.navigationFlyout,
          navigationLabel: target.innerText,
          navigationCore: 'Shop',
          sectionId: 'shop',
          sourceCode
        });
      }
    });
  }
}

function handleDealerLocatorEvents(data: any) {
  const { name, path, url, referrer, date, locale, utmMedium, utmChannel, utmCampaign, utmSource, productData, utmId, utmTerm, sourceCode } = data;
  const triggerAvoDealerSearched = (action: string, ctaText: string, ctaLocation: string, position: number | null = null, category: string | null = null, sku: string | null = null) => {
    Avo.dealerSearched({
      path,
      url,
      referrer,
      utmMedium: utmMedium || '',
      utmChannel: utmChannel || '',
      utmCampaign: utmCampaign || '',
      utmId,
      utmTerm,
      date: new Date().toISOString(),
      utmSource: utmSource || '',
      locale: locale || 'en_US',
      action,
      category,
      ctaText,
      position,
      ctaLocation: ctaLocation || '',
      sku,
      name,
      sectionId: 'shop',
      sourceCode
    });
  };

  document.querySelector('footer')?.addEventListener('click', () => {
    localStorage.setItem('dealerSource', 'footer');
  });

  document.querySelector('svg.icon-dealer')?.addEventListener('click', () => {
    localStorage.setItem('dealerSource', 'search');
  });

  const submitButton = document.getElementById('dealerLocatorSubmit');
  const useLocationContainer = document.getElementById('use-location__container');
  const dealerFormInput = document.getElementById('dealerFormInput');

  const dealerSearchTrigger = () => {
    loadSegmentAnalytics();
    const source = localStorage.getItem('dealerSource') || 'search';
    triggerAvoDealerSearched('Dealers', 'Search', source);
  };

  submitButton?.addEventListener('click', dealerSearchTrigger);
  useLocationContainer?.addEventListener('click', (event) => {
    event.stopPropagation();
    dealerSearchTrigger();
  });
  dealerFormInput?.addEventListener('keydown', (event) => {
    if (event.key === 'Enter') {
      dealerSearchTrigger();
    }
  });

  document.getElementById('dealer-locator-react')?.addEventListener('click', (event) => {
    const target = event.target as Element;
    const element = target.closest('[data-distributor-clicked]');
    const continent = target.closest('[data-continent]')?.getAttribute('data-continent') || '';
    const position = target.closest('[data-position]')?.getAttribute('data-position');

    if (element) {
      triggerAvoDealerSearched('Distributors', 'View Region', continent, position ? parseInt(position) : 1);
    }
  });

  const handleDealerSearch = (event: Event) => {
    const target = event.target as Element;
    const clickElement = target.closest('#dealerLocatorSubmit') || target.closest('#use-location__container');
    const keydownElement = target.closest('#dealerFormInput');

    const isClickEvent = event.type === 'click' && clickElement;
    const isKeydownEvent = event.type === 'keydown' && keydownElement && (event as KeyboardEvent).key === 'Enter';

    if (isClickEvent || isKeydownEvent) {
      triggerAvoDealerSearched('Dealers', 'Search', 'PDP', null, productData?.type || null, productData?.variants[0]?.sku || null);
    }
  };

  const modalElement = document.getElementById('dealer-portal-pdp-modal');
  modalElement?.addEventListener('click', handleDealerSearch);
  modalElement?.addEventListener('keydown', handleDealerSearch);

  document.getElementById('omacronlink')?.addEventListener('click', () => {
    triggerAvoDealerSearched('Distributors', 'Find Online', 'PDP', null, productData?.type || null, productData?.variants[0]?.sku || null);
  });

  document.getElementById('dealer-finder')?.addEventListener('click', (event) => {
    const target = event.target as Element;
    const linkElement = target.closest('a[data-component-id="linkWrapper"]') as HTMLAnchorElement;

    if (linkElement) {
      const links = Array.from(document.querySelectorAll('#dealer-finder a[data-component-id="linkWrapper"]'));
      const position = links.indexOf(linkElement) + 1;
      const ctaText = linkElement.href;

      triggerAvoDealerSearched('Dealers', ctaText, 'All Access Dealers', position);
    }
  });
}

function handleProductSearched(data: any) {
  const { name, path, url, referrer, date, locale, utmMedium, utmChannel, utmCampaign, utmSource, utmTerm, utmId, sourceCode } = data;

  const searchContainer = document.getElementById('bc-instant-search');
  if (searchContainer) {
    searchContainer.addEventListener('click', (event) => {
      loadSegmentAnalytics();
      const searchInput = document.getElementById('boost-sd__search-widget-init-input-2') as HTMLInputElement;
      let searchQuery = searchInput ? searchInput.value : '';

      const target = event.target as HTMLElement;

      if (target.tagName === 'A') {
        const commonQuery = target.innerText;
        Avo.productSearched({
          path,
          url,
          referrer,
          utmMedium,
          utmChannel,
          utmCampaign,
          utmId,
          utmTerm,
          date,
          utmSource,
          locale: locale || 'en_US',
          query: searchQuery,
          commonQuery: commonQuery,
          name,
          sectionId: 'shop',
          sourceCode
        });
      }

      const productItem = target.closest('.boost-sd__suggestion-queries-item--product');
      if (productItem) {
        const itemTitleElement = productItem.querySelector('.boost-sd__suggestion-queries-item-title');
        const itemTitle = itemTitleElement ? itemTitleElement.textContent?.trim() : 'Title not found';
        Avo.productSearched({
          path,
          url,
          referrer,
          utmMedium,
          utmChannel,
          utmCampaign,
          utmId,
          utmTerm,
          date,
          utmSource,
          locale: locale || 'en_US',
          query: searchQuery,
          commonQuery: itemTitle || 'View All Products',
          name,
          sectionId: 'shop',
          sourceCode
        });
      }
    });
  }
}

// ---- Filter tracking via URL change -----------------------------------------

let currentSearch = window.location.search;
let previousPfParams: { [key: string]: string } = {};

const paramKeyMapping = {
  'pf_m_::custom::series': 'Series',
  'pf_m_::custom::subtype': 'Subtype',
  'pf_p_price': 'Price Range',
  'pf_pt_product_type': 'Product Type',
  'pf_st_stock_status': 'Stock Status',
};

const paramValueMapping = {
  'in-stock': 'In Stock',
  'out-of-stock': 'Out of Stock',
  'Personal+Monitor+Mixer': 'Personal Monitor Mixer',
};

function makeHumanReadable(key: keyof typeof paramKeyMapping, value: any) {
  const readableKey = (paramKeyMapping as any)[key] || key;
  const readableValue = (paramValueMapping as any)[value] || decodeURIComponent(value);
  return { readableKey, readableValue };
}

function handleFilterChange(commonData: any) {
  const newSearch = window.location.search;
  if (currentSearch !== newSearch) {
    onUrlChange(newSearch, commonData as any);
    currentSearch = newSearch;
  }
}

function onUrlChange(queryString: string | string[][] | Record<string, string> | URLSearchParams | undefined, commonData: any) {
  const params = new URLSearchParams(queryString);
  let { name, path, url, referrer, date, locale, utmMedium, utmChannel, utmCampaign, utmSource, category, utmTerm, utmId, sourceCode } = commonData;
  const pfParams: { [key: string]: string } = {};
  params.forEach((value, key) => {
    if (key.startsWith('pf_')) {
      pfParams[key] = value;
    }
  });

  const changedParams = getChangedParams(pfParams);

  if (changedParams.length > 0) {
    loadSegmentAnalytics();
    const base = {
      path, url, referrer, utmMedium, utmChannel, utmCampaign, date, utmSource, locale, category, name, utmTerm, utmId, sourceCode
    };

    changedParams.forEach(({ key, value }) => {
      const { readableKey, readableValue } = makeHumanReadable(key as keyof typeof paramKeyMapping, value);

      handleFilterClicked({
        ...base,
        filterLabel: readableValue,
        filterGroup: readableKey,
        filterType: 'text',
      });
    });

    previousPfParams = { ...pfParams };
  }
}

function getChangedParams(currentPfParams: { [key: string]: string }) {
  const changedParams: { key: string; value: any; }[] = [];
  Object.keys(currentPfParams).forEach(key => {
    if (currentPfParams[key] !== previousPfParams[key]) {
      changedParams.push({ key, value: currentPfParams[key] });
    }
  });
  return changedParams;
}

function handleFilterClicked(data: any) {
  const { name, path, url, referrer, utmMedium, utmChannel, utmCampaign, date, utmSource, locale, category, filterLabel, filterGroup, filterType, utmTerm, utmId, sourceCode } = data;

  Avo.filterClicked({
    path,
    url,
    referrer,
    utmMedium,
    utmChannel,
    utmCampaign,
    utmId,
    utmTerm,
    date,
    utmSource,
    locale,
    filterLabel,
    filterGroup,
    category,
    name,
    sectionId: 'shop',
    sourceCode
  });
}

function initializePreviousPfParams() {
  const params = new URLSearchParams(window.location.search);
  params.forEach((value, key) => {
    if (key.startsWith('pf_')) {
      previousPfParams[key] = value;
    }
  });
}

window.addEventListener('popstate', handleFilterChange);

(function (history) {
  const pushState = history.pushState;
  const replaceState = history.replaceState;

  history.pushState = function (state) {
    pushState.apply(history, arguments as unknown as [any, string, (string | URL | null | undefined)?]);
    const commonData = collectCommonData();
    handleFilterChange(commonData);
  };

  history.replaceState = function (state) {
    replaceState.apply(history, arguments as unknown as [any, string, (string | URL | null | undefined)?]);
    const commonData = collectCommonData();
    handleFilterChange(commonData);
  };
})(window.history);

// ---- Promotion & Recommendation ---------------------------------------------

function handlePromotionClicked(data: any) {
  const { name, path, url, referrer, date, locale, utmMedium, utmChannel, utmCampaign, utmSource, utmTerm, utmId, sourceCode } = data;

  document.body.addEventListener('click', (event) => {
    const element = (event.target as HTMLElement).closest('[data-promotion-clicked]');

    if (!element) return;

    loadSegmentAnalytics();
    const ctaText = element.getAttribute('data-cta-text') || '';
    const ctaTargetUrl = element.getAttribute('data-cta-url') || '';

    let nearestImg: HTMLImageElement | null = null;
    let currentElement: any = element;

    while (currentElement && !nearestImg) {
      nearestImg = currentElement.querySelector('img');
      currentElement = currentElement ? (currentElement.parentElement as Element) : null;
    }

    const creative = nearestImg ? nearestImg.src : '';
    const nearestComponent = element.closest('[data-component-id]');
    const ctaLocation = nearestComponent ? nearestComponent.getAttribute('data-component-id') : '';
    const position = element ? Array.from(document.querySelectorAll('[data-promotion-clicked]')).indexOf(element) + 1 : -1;

    Avo.promotionClicked({
      path,
      url,
      referrer,
      utmMedium,
      utmChannel,
      utmCampaign,
      utmId,
      utmTerm,
      date,
      utmSource,
      locale,
      ctaText,
      ctaLocation: ctaLocation || '',
      name,
      creative,
      position,
      ctaTargetUrl,
      sectionId: 'shop',
      sourceCode
    });
  }, { once: true });
}

function handleRecommendationClicked(data: any) {
  const { name, path, url, referrer, date, locale, utmMedium, utmChannel, utmCampaign, utmSource, utmTerm, utmId, sourceCode } = data;

  document.body.addEventListener('click', (event) => {
    const element = (event.target as HTMLElement).closest('[data-recommendation-clicked]');
    const sku = element?.getAttribute('data-sku') || null;
    const category = element?.getAttribute('data-category') || '';
    const productName = element?.getAttribute('data-product-name') || null;
    const productType = category.includes('Software') || category.includes('Subscription') ? 'digital' : 'physical';
    const source = element?.getAttribute('data-component-id') || '';
    const position = element ? Array.from(document.querySelectorAll('[data-recommendation-clicked]')).indexOf(element) + 1 : -1;

    if (!element) return;

    loadSegmentAnalytics();

    Avo.recommendationClicked({
      path,
      url,
      referrer,
      utmMedium,
      utmChannel,
      utmCampaign,
      utmId,
      utmTerm,
      date,
      utmSource,
      locale,
      name,
      position,
      productName,
      productType,
      sku,
      category,
      source,
      headerName: '',
      sectionId: 'shop',
      sourceCode
    });
  });
}

// ---- Auth (kept minimal / commented Avo calls as in original) ---------------

let isLoggedIn = false;
const handleLoggedIn = (data: any) => {
  const { /*name, path, url, referrer, date, locale, utmMedium, utmChannel, utmCampaign, utmSource,*/ prsID, customer } = data;
  if (isLoggedIn) return;
  if (document.referrer?.includes('connect') && (!!customer)) {
    // Wire up when ready; include sourceCode from data if enabling:
    // Avo.loggedIn({...data, shopifyId: customer, userId_: prsID, sectionId: 'shop', sourceCode: data.sourceCode });
    isLoggedIn = true;
  }
};

const handleLoggedOut = (data: any) => {
  document.querySelector('[data-logged-out]')?.addEventListener('click', () => {
    loadSegmentAnalytics();
    // Avo.loggedOut({...data, sectionId: 'shop', sourceCode: data.sourceCode });
    localStorage.removeItem('prsID');
    segmentAnalytics.reset();
  });
};

// ---- Notify In Stock ---------------------------------------------------------

const handleNotifyInStock = (data: any) => {
  const { path, url, referrer, date, locale, utmMedium, utmChannel, utmCampaign, utmSource, productData, currency, category, utmId, utmTerm, sourceCode } = data;
  document.addEventListener("click", (event) => {
    const price = productData?.price || 0;
    const name = productData?.title || '';
    const variant = productData?.variants[0]?.id || '';

    const product: Products = {
      currency: currency,
      price: price ? parseFloat((Number(price) / 100).toFixed(2)) : 0.00,
      quantity: 1,
      variant: variant,
      category: category,
      position: 1,
      productName: name,
      productType: productData?.type?.includes('Software') || productData?.type?.includes('Subscription') ? 'digital' : 'physical',
      upsell: false,
      sku: productData?.variants[0]?.sku || '',
      productId: productData?.id
    };

    const target = event.target as HTMLElement;
    if (target?.classList.contains("restock-alerts-submit-button")) {
      loadSegmentAnalytics();
      Avo.notifyInStock({
        name,
        path,
        url,
        referrer,
        date,
        locale,
        utmMedium,
        utmChannel,
        utmCampaign,
        utmId,
        utmTerm,
        utmSource,
        products: [product],
        categoryTag: productData.tags || [] as string[],
        sectionId: 'shop',
        sourceCode
      });
    }
  });
};


const SUPPRESS_TTL_MS = 60_000; 
function suppressCartDelta(vid: string, qty: number) {
  const key = String(vid);
  suppressCartDeltaOnce[key] = (suppressCartDeltaOnce[key] || 0) + Math.max(1, Number(qty) || 1);

  setTimeout(() => { delete suppressCartDeltaOnce[key]; }, SUPPRESS_TTL_MS);
}

function handleProductAdded(data: any) {
  const {
    productData, currency, path, url, referrer, date, locale,
    utmMedium, utmChannel, utmCampaign, utmSource, utmId, utmTerm,
    sectionID, sourceCode
  } = data;

  if (!productData) return;

  const getVariantFromQueryString = () => {
    const params = new URLSearchParams(window.location.search);
    return (
      params.get('variant') ||
      productData?.variants?.[0]?.id?.toString() ||
      ''
    );
  };

  function getProductTitle() {
    let titleElement = document.querySelector('.product__title h1') ||
                       document.querySelector('.product__title a h2');
    return titleElement && titleElement.textContent
      ? titleElement.textContent.trim()
      : productData?.title || '';
  }

  function getSkuByVariant(variantId?: string | number | null) {
    const vid = String(variantId ?? getVariantFromQueryString());
    const selectedVariant = productData?.variants?.find(
      (v: any) => String(v.id) === vid
    );
    return selectedVariant ? selectedVariant.sku : '';
  }

  function getPrice() {
    let priceElement =
      document.querySelector('.price-main .price__sale, .price-main .price-item--sale') ||
      document.querySelector('.price-main .price-item--regular, .price-main .money');
    if (!priceElement || !priceElement.textContent) return 0;

    const rawText = priceElement.textContent.trim();
    const match = rawText.match(/[\d.,]+/);
    if (!match) return 0;

    const numericChunk = match[0];
    const lastDotPos = numericChunk.lastIndexOf('.');
    const lastCommaPos = numericChunk.lastIndexOf(',');

    if (lastDotPos === -1 && lastCommaPos === -1) {
      const parsed = parseFloat(numericChunk);
      return isNaN(parsed) ? 0 : round2(parsed);
    }

    const decimalSeparator = lastDotPos > lastCommaPos ? '.' : ',';
    const idx = decimalSeparator === '.' ? lastDotPos : lastCommaPos;

    let integerPart = idx !== -1 ? numericChunk.slice(0, idx) : numericChunk;
    let fractionPart = idx !== -1 ? numericChunk.slice(idx + 1) : '0';

    integerPart = integerPart.replace(/[.,]/g, '');
    const normalized = `${integerPart}.${fractionPart}`;
    const parsed = parseFloat(normalized);
    return isNaN(parsed) ? 0 : round2(parsed);

    function round2(n: number) { return Math.round((n + Number.EPSILON) * 100) / 100; }
  }

  document.addEventListener('click', (ev) => {
    const target = ev.target as HTMLElement | null;
    const btn = target?.closest?.('button[name="add"].product-form__submit') as HTMLButtonElement | null;
    if (!btn) return;

    try { loadSegmentAnalytics(); } catch {}

    const form = (btn.form || btn.closest('form')) as HTMLFormElement | null;
    const vid = (form?.querySelector('input[name="id"]') as HTMLInputElement | null)?.value
      || getVariantFromQueryString()
      || '';
    const qtyEl = form?.querySelector('input[name="quantity"], input[name="qty"], .quantity__input') as HTMLInputElement | null;
    const qty = qtyEl?.value ? Math.max(1, parseInt(qtyEl.value, 10)) : 1;

    const product = {
      currency: currency || 'USD',
      price: getPrice() || 0,
      quantity: qty,
      variant: String(vid),
      category: productData?.type || '',
      position: 1,
      productName: getProductTitle(),
      productType:
        productData?.type?.includes('Software') || productData?.type?.includes('Subscription')
          ? 'digital'
          : 'physical',
      upsell: false,
      sku: getSkuByVariant(vid),
      productId: String(productData?.id || '')
    };

    if (location.href.includes('garage')) {
      console.log('[PDP click] productAdded fire', product);
    }

    Avo.productAdded({
      path, url, referrer, utmMedium, utmChannel, utmCampaign, utmId, utmTerm, date, utmSource, locale,
      products: [product],
      categoryTag: productData?.tags || [],
      name: product.productName,
      sectionId: sectionID,
      sourceCode,
      total: product.price * product.quantity,
      subtotal: product.price * product.quantity,
      currency: product.currency,
      source: 'shopify'
    });

    suppressCartDelta(String(vid), qty);
  });
}

const lastQtyByVariant: Record<string, number> = (window as any).__lastQtyByVariant || {};
(window as any).__lastQtyByVariant = lastQtyByVariant;

const lastLineByVariant: Record<string, any> = (window as any).__lastLineByVariant || {};
(window as any).__lastLineByVariant = lastLineByVariant;

const suppressCartDeltaOnce: Record<string, number> =
  (window as any).__suppressCartDeltaOnce || ((window as any).__suppressCartDeltaOnce = {});

const suppressCartDeltaNegOnce: Record<string, number | 'all'> =
  (window as any).__suppressCartDeltaNegOnce || ((window as any).__suppressCartDeltaNegOnce = {});




type CartUpdatePayload = {
  productVariantId?: string | number;
  variantId?: string | number;
  cartData?: any;
};

const handleCartUpdateQuantityDelta = (payload: CartUpdatePayload) => {
  try { loadSegmentAnalytics(); } catch {}

  const suppressPos = (window as any).__suppressCartDeltaOnce || ((window as any).__suppressCartDeltaOnce = {});
  const suppressNeg = (window as any).__suppressCartDeltaNegOnce || ((window as any).__suppressCartDeltaNegOnce = {});

  function seedLedgerFromLocalCartSnapshot() {
    try {
      const raw = localStorage.getItem('cart');
      if (!raw) return;
      const cart = JSON.parse(raw);
      if (!cart || !Array.isArray(cart.items)) return;
      for (const it of cart.items) {
        const vid = String(it?.variant_id ?? it?.id ?? '');
        if (!vid) continue;
        (window as any).__lastQtyByVariant = (window as any).__lastQtyByVariant || {};
        (window as any).__lastLineByVariant = (window as any).__lastLineByVariant || {};
        lastQtyByVariant[vid] = Number(it?.quantity || 0);
        (window as any).__lastLineByVariant[vid] = it;
      }
    } catch {}
  }
  if (Object.keys(lastQtyByVariant).length === 0) seedLedgerFromLocalCartSnapshot();

  interface LineLike {
    variant_id?: string | number;
    id?: string | number;
    quantity?: number;
    product_type?: string;
    product_title?: string;
    title?: string;
    sku?: string;
    product_id?: string | number;
    line_price?: number;
    price?: number;
    tags?: string[];
  }
  
    const getVid = (line?: LineLike): string => String(
    payload.productVariantId ?? payload.variantId ?? line?.variant_id ?? line?.id ?? ''
  );

  const data = payload.cartData as any;
  const currency = (data?.currency || data?.currency_code || 'USD') as string;

  const isFullCart = !!(data && typeof data === 'object' && 'items' in data);
  const changes: Array<{ vid: string; delta: number; qtyNow: number; line?: any }> = [];

  if (isFullCart) {
    interface LineLike {
        variant_id?: string | number;
        id?: string | number;
        quantity?: number;
        product_type?: string;
        product_title?: string;
        title?: string;
        sku?: string;
        product_id?: string | number;
        line_price?: number;
        price?: number;
        tags?: string[];
    }

    const items: LineLike[] = Array.isArray(data.items) ? data.items : [];
    const seen = new Set<string>();

    for (const line of items) {
      const vid = getVid(line);
      if (!vid) continue;
      seen.add(vid);

      const prev = Number(lastQtyByVariant[vid] ?? 0);
      const now = Number(line.quantity || 0);
      const delta = now - prev;

      (window as any).__lastLineByVariant[vid] = line;
      if (delta !== 0) changes.push({ vid, delta, qtyNow: now, line });
    }

    for (const vid in lastQtyByVariant) {
      if (!seen.has(vid) && lastQtyByVariant[vid] > 0) {
        const prev = lastQtyByVariant[vid];
        const cached = (window as any).__lastLineByVariant?.[vid] || { variant_id: vid, quantity: 0 };
        changes.push({ vid, delta: -prev, qtyNow: 0, line: cached });
      }
    }

    for (const line of items) {
      const vid = getVid(line);
      if (vid) lastQtyByVariant[vid] = Number(line.quantity || 0);
    }
    for (const vid in lastQtyByVariant) {
      if (!seen.has(vid)) delete lastQtyByVariant[vid];
    }
  } else {
    interface LineLike {
      variant_id?: string | number;
      id?: string | number;
      quantity?: number;
      product_type?: string;
      product_title?: string;
      title?: string;
      sku?: string;
      product_id?: string | number;
      line_price?: number;
      price?: number;
      tags?: string[];
    }
    
        const line = (data || {}) as LineLike;
    const vid = getVid(line);
    if (vid) {
      if (lastQtyByVariant[vid] === undefined) seedLedgerFromLocalCartSnapshot();
      const prevKnown = lastQtyByVariant[vid];

      const now = Number(line.quantity || 0);
      if (prevKnown === undefined) {
        lastQtyByVariant[vid] = now;
        (window as any).__lastLineByVariant[vid] = line;
        if (location.href.includes('garage')) console.log('[cartUpdate Δ] baseline set', vid, now);
        return;
      }

      const prev = Number(prevKnown || 0);
      const delta = now - prev;

      if (now > 0) {
        lastQtyByVariant[vid] = now;
        (window as any).__lastLineByVariant[vid] = line;
      } else {
        delete lastQtyByVariant[vid];
      }

      if (delta !== 0) changes.push({ vid, delta, qtyNow: now, line });
    }
  }

  if (!changes.length) return;

  const path = location.pathname;
  const url = location.href;
  const referrer = document.referrer || '';
  const date = new Date().toISOString();
  const locale = (window as any).Shopify?.locale || document.documentElement.lang || 'en-US';
  const attr: any = getAttribution();
  const utmMedium   = attr.utm_medium || null;
  const utmChannel  = attr.utm_channel || null;
  const utmCampaign = attr.utm_campaign || null;
  const utmSource   = attr.utm_source || null;
  const utmId       = attr.utm_id || null;
  const utmTerm     = attr.utm_term || null;
  const sourceCode  = getSourceCode();

  const toUnitPrice = (line: any, qtyForMath: number) =>
    unitPriceFromLine(line, Math.max(1, qtyForMath));

  for (const { vid, delta, qtyNow, line } of changes) {
    let d = delta;

    if (d > 0 && suppressPos[vid]) {
      const suppressed = Math.min(d, Number(suppressPos[vid]) || 0);
      d = d - suppressed;
      if (suppressed >= Number(suppressPos[vid])) delete suppressPos[vid];
      else suppressPos[vid] = Number(suppressPos[vid]) - suppressed;
    }

    if (d < 0 && suppressNeg[vid] != null) {
      const want = Number(suppressNeg[vid]) || Infinity;
      const suppressed = Math.min(-d, want);
      const remainder = (-d) - suppressed;
      if (!isFinite(want) || suppressed >= want) delete suppressNeg[vid];
      else suppressNeg[vid] = want - suppressed;
      d = remainder > 0 ? -remainder : 0;
    }

    if (d === 0) continue;

    const product = {
      currency,
      price: toUnitPrice(line || (window as any).__lastLineByVariant?.[vid], qtyNow || 1),
      quantity: Math.abs(d),
      variant: String(line?.variant_id ?? line?.id ?? vid),
      category: line?.product_type || '',
      position: 1,
      productName: line?.product_title || line?.title || '',
      productType:
        (line?.product_type || '').includes('Software') ||
        (line?.product_type || '').includes('Subscription')
          ? 'digital'
          : 'physical',
      upsell: false,
      sku: line?.sku || '',
      productId: String(line?.product_id ?? ''),
    };

    if (location.href.includes('garage')) {
      console.log('[cartUpdate Δ resolved]', { vid, d, product, rawDelta: delta });
    }

    if (d > 0) {
      Avo.productAdded({
        path, url, referrer, utmMedium, utmChannel, utmCampaign, utmId, utmTerm, date, utmSource, locale,
        products: [product],
        categoryTag: (line?.tags as any) || [],
        name: product.productName,
        sectionId: '',
        sourceCode,
        total: product.price * product.quantity,
        subtotal: product.price * product.quantity,
        currency: product.currency,
        source: 'shopify'
      });
    } else {
      Avo.productRemoved?.({
        path, url, referrer, utmMedium, utmChannel, utmCampaign, utmId, utmTerm, date, utmSource, locale,
        products: [product],
        categoryTag: (line?.tags as any) || [],
        name: product.productName,
        sectionId: '',
        sourceCode,
        total: product.price * product.quantity,
        subtotal: product.price * product.quantity,
        currency: product.currency,
        source: 'shopify'
      });
    }
  }
};


function unitPriceFromLine(line: any, qtyForMath: number): number {
  const cents = typeof line?.price === 'number'
    ? line.price
    : (typeof line?.line_price === 'number' && qtyForMath > 0)
      ? Math.round(line.line_price / qtyForMath)
      : 0;
  return parseFloat(((cents || 0) / 100).toFixed(2));
}

async function getCartLineForVariant(vid: string): Promise<{ line: any|null; currency: string }> {
  try {
    const raw = localStorage.getItem('cart');
    if (raw) {
      const cart = JSON.parse(raw);
      const line = (cart?.items || []).find((i: any) => String(i.variant_id ?? i.id) === String(vid)) || null;
      const currency = cart?.currency || cart?.currency_code || 'USD';
      if (line) return { line, currency };
    }
  } catch {}
  try {
    const r = await fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } });
    if (r.ok) {
      const cart = await r.json();
      const line = (cart?.items || []).find((i: any) => String(i.variant_id ?? i.id) === String(vid)) || null;
      const currency = cart?.currency || cart?.currency_code || 'USD';
      return { line, currency };
    }
  } catch {}
  return { line: null, currency: 'USD' };
}

function handleRemoveFromCart() {
  document.addEventListener('click', (ev) => {
    const a = (ev.target as HTMLElement)?.closest?.('a.cart-remove-button') as HTMLAnchorElement | null;
    if (!a) return;

    const u = new URL(a.href, location.origin);
    const idParam = u.searchParams.get('id') || '';
    const vid = idParam.split(':')[0];
    if (!vid) return;

    const qtyNow = Number((window as any).__lastQtyByVariant?.[vid] ?? 0);
    suppressCartDeltaNegOnce[vid] = qtyNow || 'all';

    (async () => {
      try { loadSegmentAnalytics(); } catch {}
      const { line, currency } = await getCartLineForVariant(vid);
      const qty = Number(line?.quantity || qtyNow || 1);

      const product = {
        currency: currency || 'USD',
        price: unitPriceFromLine(line, Math.max(1, qty)),
        quantity: qty,
        variant: String(line?.variant_id ?? vid),
        category: line?.product_type || '',
        position: 1,
        productName: line?.product_title || line?.title || '',
        productType:
          (line?.product_type || '').includes('Software') ||
          (line?.product_type || '').includes('Subscription')
            ? 'digital'
            : 'physical',
        upsell: false,
        sku: line?.sku || '',
        productId: String(line?.product_id ?? '')
      };

      const attr: any = getAttribution();
      Avo.productRemoved?.({
        path: location.pathname,
        url: location.href,
        referrer: document.referrer || '',
        utmMedium: attr.utm_medium || null,
        utmChannel: attr.utm_channel || null,
        utmCampaign: attr.utm_campaign || null,
        utmId: attr.utm_id || null,
        utmTerm: attr.utm_term || null,
        date: new Date().toISOString(),
        utmSource: attr.utm_source || null,
        locale: (window as any).Shopify?.locale || document.documentElement.lang || 'en-US',
        products: [product],
        categoryTag: (line?.tags as any) || [],
        name: product.productName,
        sectionId: '',
        sourceCode: getSourceCode(),
        total: product.price * product.quantity,
        subtotal: product.price * product.quantity,
        currency: product.currency,
        source: 'shopify'
      });
    })().catch(() => {});
  }, { capture: true });
}



// Bind after the function is defined (do NOT wait for window.onload)
subscribe(PUB_SUB_EVENTS.cartUpdate, handleCartUpdateQuantityDelta);

// ---- Boot --------------------------------------------------------------------

// Persist attribution as soon as the module runs (before any analytics init)
persistAttributionOnLanding();

window.onload = () => {
  setShopifyCustomPixelValues();
  loadSegmentAnalytics();
  const commonData = collectCommonData();

  initializePreviousPfParams();
  handleProductListViewed(commonData);
  handleLoadedAPage(commonData);
  handleProductViewed(commonData);
  handleCartViewed(commonData);
  handleNavigationClicked(commonData);
  handleProductAdded(commonData);
  handleRemoveFromCart();
  handleDealerLocatorEvents(commonData);
  handleProductSearched(commonData);
  handleFilterChange(commonData);
  handlePromotionClicked(commonData);
  handleLoggedIn(commonData);
  handleLoggedOut(commonData);
  handleRecommendationClicked(commonData);
  handleNotifyInStock(commonData);
};
